// #3478 - pure helpers behind the Review Queue's Sheet versions screen
// (GLOSSARY "Sheet version", ADR 0002). A sheet version is one of the two or
// more different scoresheets the cloud holds for one judge on one ride. Two
// shapes reach the screen, and both are turned into the same "version" object
// here so the screen renders one list:
//
//  - a HELD COPY: the sheet row plus the whole copy a tablet save left held on
//    it (staleWriteConflictAttempted). Both versions are picked through the
//    SAME sheet id, with version "sheet" (keep the sheet) or "held_copy".
//  - a CONTENDER: the sheets at one seat (judgeId) of one ride. The one that
//    stands is authoritative; picking any other sends ITS id with version
//    "sheet".
//
// No React, no fetching: unit-testable on their own. The server decides what a
// pick does (apps/score-api/src/lib/sheet-version-pick.js); the mirrors of its
// signed tests below only decide what the confirm box says.
import { isAuthoritativeScoreSheet } from "score-common";
import { formatSimpleMovementText } from "./rideViewMode";
import { buildMovementDiffRows, buildSheetLevelDiffRows } from "./contenderCompare";

// The sheet-level fields a held copy / sheet can differ on, that an admin can
// read as a word. The computed totals are left out on purpose: a held copy has
// none (the server works them out when a version is picked), so they would
// always read as "different".
const SHEET_LEVEL_FIELDS_SHOWN = new Set([
  "collectives",
  "eliminations",
  "errors",
  "generalRemarks",
  "annotatedPurpose",
  "annotatedIntroduce",
]);

// A sheet ROW is signed once Complete & Sign stamped it (score-api's
// isSheetRowSigned).
export function isSheetRowSigned(sheet) {
  return Boolean(sheet?.completedAt);
}

// A HELD body is signed when the judge drew a signature into it or the tablet
// recorded a Complete request with it (score-api's isHeldCopySigned).
export function isHeldCopySigned(row) {
  const body = row?.staleWriteConflictAttempted ?? {};
  return Boolean(body.judgeSignature) || row?.staleWriteHeldIntent?.type === "complete";
}

// Does this sheet row carry a held copy a person can pick?
export function hasHeldCopy(row) {
  return row?.staleWriteConflictPending === "true" && Object.keys(row.staleWriteConflictAttempted ?? {}).length > 0;
}

function numberOrNull(n) {
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function versionFields(source) {
  return {
    movementScores: Array.isArray(source?.movementScores) ? source.movementScores : [],
    collectives: source?.collectives,
    eliminations: source?.eliminations,
    errors: source?.errors,
    generalRemarks: source?.generalRemarks,
    annotatedPurpose: source?.annotatedPurpose,
    annotatedIntroduce: source?.annotatedIntroduce,
  };
}

function sheetVersion(sheet, { key, standing, pick }) {
  return {
    key,
    kind: "sheet",
    sheetId: sheet.id,
    testId: sheet.testId ?? null,
    standing,
    pick,
    origin: sheet.provenance?.origin ?? null,
    device: sheet.provenance?.device ?? null,
    when: sheet.updatedAt ?? sheet.provenance?.capturedAt ?? null,
    total: numberOrNull(sheet.totalPoints),
    max: numberOrNull(sheet.maxPoints),
    signed: isSheetRowSigned(sheet),
    ...versionFields(sheet),
  };
}

// The sheet as it stands plus its held copy, in that order.
export function versionsFromHeldCopy(row) {
  const body = row.staleWriteConflictAttempted ?? {};
  const intent = row.staleWriteHeldIntent ?? null;
  const clientTotals = intent?.body?.clientTotals ?? intent?.clientTotals ?? null;
  return [
    sheetVersion(row, { key: "sheet", standing: true, pick: { id: row.id, version: "sheet" } }),
    {
      key: "held_copy",
      kind: "held_copy",
      sheetId: row.id,
      testId: row.testId ?? null,
      standing: false,
      pick: { id: row.id, version: "held_copy" },
      origin: "scribe",
      device: row.staleWriteHeldDevice ?? null,
      when: row.staleWriteConflictAt ?? null,
      // A held body carries no total of its own (the server computes it when
      // the version is picked); the tablet's own figure is shown when it sent
      // one with a Confirm or Complete request.
      total: numberOrNull(clientTotals?.totalPoints),
      max: numberOrNull(clientTotals?.maxPoints),
      signed: isHeldCopySigned(row),
      ...versionFields(body),
    },
  ];
}

// The sheets at one seat, the one that stands first. Only a sheet that does
// not already stand can be picked (a pick on the standing one is refused).
export function versionsFromSeat(sheets, judgeId) {
  const seat = (sheets ?? []).filter((s) => s.judgeId === judgeId);
  const ordered = [...seat].sort((a, b) => {
    const aAuth = isAuthoritativeScoreSheet(a) ? 0 : 1;
    const bAuth = isAuthoritativeScoreSheet(b) ? 0 : 1;
    if (aAuth !== bAuth) return aAuth - bAuth;
    return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
  });
  return ordered.map((sheet) => {
    const standing = isAuthoritativeScoreSheet(sheet);
    return sheetVersion(sheet, {
      key: `sheet:${sheet.id}`,
      standing,
      pick: standing ? null : { id: sheet.id, version: "sheet" },
    });
  });
}

// Everything the screen draws: the per-movement rows (differing ones marked)
// and the names of the sheet-level things that differ without being a row.
export function buildVersionsComparison(versions) {
  return {
    movementRows: buildMovementDiffRows(versions),
    otherDifferences: buildSheetLevelDiffRows(versions)
      .map((row) => row.field)
      .filter((field) => SHEET_LEVEL_FIELDS_SHOWN.has(field)),
  };
}

// Will picking `picked` move a signature onto it? True when some other version
// was signed and the picked one is not: the server then carries the signature
// (ADR 0002) and the confirm box has to say so.
export function pickCarriesSignature(versions, picked) {
  if (!picked || picked.signed) return false;
  return (versions ?? []).some((v) => v.key !== picked.key && v.signed);
}

// #3484 - the figures the server's dry run (previewSheetVersionTotals) worked
// out for the versions that arrived without a total, laid over the versions.
// A version that already has a total keeps it. `previews` is keyed by the
// version's `key`.
export function withPreviewedTotals(versions, previews) {
  return (versions ?? []).map((v) => {
    if (v.total !== null) return v;
    const p = previews?.[v.key];
    const total = numberOrNull(p?.totalPoints);
    if (total === null) return v;
    return { ...v, total, max: numberOrNull(p?.maxPoints), totalFromPreview: true };
  });
}

// The versions that need a dry-run total: no figure of their own, and a pick to
// ask about (the standing sheet of a contender pair has no pick; its stored
// figures are all there is).
export function versionsNeedingPreview(versions) {
  return (versions ?? []).filter((v) => v.total === null && v.pick);
}

// The name of a movement, read off the sheet's Test, in the words the rest of
// the app shows ("a | b" becomes "a · b"). Empty when the Test is not loaded
// or does not list the movement.
export function movementNameFromTest(test, movementNumber) {
  const m = (test?.movements ?? []).find((x) => x && x.movementNumber === movementNumber);
  return formatSimpleMovementText(m?.description);
}
